import { Suspense } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

import { HomeyError } from './lib/InternalError';

import { useI18n } from './hooks/useI18nFormatters';
import { useAuth } from './store/AuthStore';
import { useSelectedHomeyState } from './store/SelectedHomeyStore';

import { authStore } from './store/AuthStore';
import { selectedHomeyStore } from './store/SelectedHomeyStore';

import { theme } from './theme/theme';

import { SolidButton } from './components/buttons/SolidButton';
import { SpinnerLoading } from './components/common/SpinnerLoading';
import { CenteredMessage } from './components/common/CenteredMessage';
import { Icon } from './components/common/Icon';

import { LoginPage } from './containers/LoginPage';
import { LocalLoginPage } from './containers/LocalLoginPage';
import { InvitePage } from './containers/InvitePage';
import {
  HomePage,
  DevicesPage,
  FlowsPage,
  InsightsPage,
  ScriptPage,
  LogicPage,
  SettingsPage,
} from './containers/dynamic';
import { TestPage } from './containers/TestPage';
import { UpdateRebootPage } from './containers/UpdateRebootPage';

import { DragLayer } from './components/dnd/DragLayer';
import { DialogPrompt } from './components/overlay/DialogPrompt';
import { Toasts } from './ToastManager';
import { ErrorBoundary } from './components/ErrorBoundary';

import { Layout } from './Layout';

import { iconWarningCircle } from './theme/icons/interface/warning-circle/warning-circle';

export function App() {
  const { isRebooting, isUpdating } = useSelectedHomeyState();

  return (
    <ErrorBoundary>
      <Switch>
        <Route path={`${import.meta.env.BASE_URL}login-local`}>
          <LocalLoginPage />
        </Route>

        <Route path={`${import.meta.env.BASE_URL}login`}>
          <LoginPage />
        </Route>

        <Route path="/">
          <App.Root>
            {(() => {
              if (isUpdating === true || isRebooting === true) {
                return <UpdateRebootPage />;
              }

              return (
                <Switch>
                  <Route
                    path="/invite"
                    render={() => {
                      return (
                        <AuthenticatedRoute>
                          <InvitePage />
                        </AuthenticatedRoute>
                      );
                    }}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/devices`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <DevicesPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}devices`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/flows`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <FlowsPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}flows`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/insights`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <InsightsPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}insights`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/scripts`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <ScriptPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}scripts`}
                    render={() => <HomeyRoute redirect />}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}script`}
                    render={() => <Redirect to={`${import.meta.env.BASE_URL}scripts`} />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/logic`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <LogicPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}logic`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/settings`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <SettingsPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}settings`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId/test`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <TestPage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    path={`${import.meta.env.BASE_URL}test`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path={`${import.meta.env.BASE_URL}homeys/:homeyId`}
                    render={() => (
                      <HomeyRoute>
                        <Suspense fallback={null}>
                          <HomePage />
                        </Suspense>
                      </HomeyRoute>
                    )}
                  />
                  <Route
                    exact
                    path={`${import.meta.env.BASE_URL}`}
                    render={() => <HomeyRoute redirect />}
                  />

                  <Route
                    path="*"
                    render={() => {
                      return <NoMatch />;
                    }}
                  />
                </Switch>
              );
            })()}
          </App.Root>
        </Route>
      </Switch>

      {/* Renders some dragged components in a fixed layer. */}
      <DragLayer />

      {/* Prompt for confirmation. */}
      <DialogPrompt />

      <Toasts />
    </ErrorBoundary>
  );
}

App.Root = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  position: relative;
  min-width: 720px;
  max-width: 100vw;
  height: 100%;
  max-height: 100vh;
  overflow: hidden;
`;

function AuthenticatedRoute(props) {
  const { i18n } = useI18n();
  const location = useLocation();
  const auth = useAuth();

  // The error check is temporary until the whole auth flow has been redone. We cant render the
  // error on the invite page because people might have no homeys and should still be able to
  // accept invites.
  if (auth.error != null && !(auth.error instanceof HomeyError)) {
    return renderAuthError({ auth, i18n });
  }

  // No user and resolved redirect to login.
  if (auth.cloudUser == null && auth.resolved === true) {
    window.location.href = authStore.getLoginUrl(location);
    return null;
  }

  if (auth.loading === true || auth.resolved === false) {
    return (
      <CenteredMessage
        icon={<SpinnerLoading color={theme.color.text_light} size={40} />}
        subtitleColor={theme.color.text_light}
        subtitle={i18n.messageFormatter(`status.loading`)}
      />
    );
  }

  if (auth.cloudUser != null) {
    return props.children;
  }

  // No case here should not be possible.
  console.warn('Missing implementation for: ', { auth });

  return (
    <CenteredMessage
      icon={<SpinnerLoading color={theme.color.text_light} size={40} />}
      subtitleColor={theme.color.text_light}
      subtitle={i18n.messageFormatter(`status.loading`)}
    />
  );
}

function HomeyRoute(props) {
  const { i18n } = useI18n();
  const location = useLocation();
  const auth = useAuth();
  const selectedHomeyState = useSelectedHomeyState();

  if (auth.error != null) {
    return renderAuthError({ auth, selectedHomeyState, i18n });
  }

  if (selectedHomeyState.error != null) {
    console.error(selectedHomeyState.error);

    switch (selectedHomeyState.error?.message) {
      case 'user_disabled':
      case 'User Is Disabled': {
        return (
          <Layout
            content={
              <CenteredMessage
                title={i18n.messageFormatter('homeyStatus.userdisabled.title')}
                subtitle={i18n.messageFormatter('homeyStatus.userdisabled.subtitle')}
              />
            }
          />
        );
      }

      case 'homey_offline':
      case 'Homey Offline': {
        return (
          <Layout
            content={
              <CenteredMessage
                title={i18n.messageFormatter('homeyStatus.offline.title')}
                subtitle={i18n.messageFormatter('homeyStatus.offline.subtitle')}
                action={
                  <SolidButton
                    styleColor="green"
                    isDisabled={selectedHomeyState.loading === true}
                    onPress={() => {
                      selectedHomeyStore.retry();
                    }}
                  >
                    {i18n.messageFormatter('homeyStatus.offline.button')}
                  </SolidButton>
                }
              />
            }
          />
        );
      }

      case 'Subscription Inactive': {
        return (
          <Layout
            content={
              <CenteredMessage
                icon={<Icon url={iconWarningCircle} color={theme.color.warning} size="40px" />}
                title={i18n.messageFormatter('homeyStatus.subscriptionInactive.title')}
                subtitle={i18n.messageFormatter('homeyStatus.subscriptionInactive.subtitle')}
              />
            }
          />
        );
      }

      default:
        return (
          <Layout
            content={
              <CenteredMessage
                title="Error"
                subtitle={selectedHomeyState.error?.message ?? 'Unknown error occurred'}
                action={
                  <SolidButton
                    styleColor="green"
                    isDisabled={selectedHomeyState.loading === true}
                    onPress={() => {
                      selectedHomeyStore.retry();
                    }}
                  >
                    {i18n.messageFormatter('common.retry')}
                  </SolidButton>
                }
              />
            }
          />
        );
    }
  }

  // No user and resolved redirect to login.
  if (import.meta.env.MODE !== 'self-hosted' && auth.cloudUser == null && auth.resolved === true) {
    window.location.href = authStore.getLoginUrl(location);
    return null;
  }

  // User and visited non homeyId path.
  if (auth.cloudUser != null && props.redirect) {
    const currentHomeyId = authStore.getCurrentHomeyId(auth.cloudUser);

    if (currentHomeyId == null) {
      return renderHomeyNotFound({ auth, selectedHomeyState, i18n });
    }

    const localPath = location.pathname.substring(import.meta.env.BASE_URL.length);
    return <Redirect to={`${import.meta.env.BASE_URL}homeys/${currentHomeyId}/${localPath}`} />;
  }

  if (auth.cloudUser != null && selectedHomeyState.selectedHomey != null) {
    return props.children;
  }

  if (auth.loading === true || selectedHomeyState.loading === true || auth.resolved === false) {
    return <LoadingPage message={i18n.messageFormatter(`status.loading`)} />;
  }

  if (
    import.meta.env.MODE === 'self-hosted' &&
    selectedHomeyState.selectedHomey?.id != null &&
    props.redirect
  ) {
    const localPath = location.pathname.substring(import.meta.env.BASE_URL.length);
    return (
      <Redirect
        to={`${import.meta.env.BASE_URL}homeys/${selectedHomeyState.selectedHomey.id}/${localPath}`}
      />
    );
  }

  if (import.meta.env.MODE === 'self-hosted' && selectedHomeyState.selectedHomey?.id != null) {
    return props.children;
  }

  // No case here should not be possible.
  console.warn('Missing implementation for: ', { auth, selectedHomeyState });

  return <LoadingPage message={i18n.messageFormatter(`status.loading`)} />;
}

function renderAuthError({ auth, selectedHomeyState, i18n }) {
  console.error(auth.error);

  switch (auth.error.constructor) {
    case HomeyError.NotFound:
      return renderHomeyNotFound({ auth, selectedHomeyState, i18n });
    default:
      break;
  }

  switch (auth.error) {
    default:
      return renderImplementError({ auth, selectedHomeyState, i18n });
  }
}

function renderHomeyNotFound({ auth, i18n }) {
  if (auth.cloudUser?.homeys?.length === 0) {
    return (
      <Layout
        content={
          <CenteredMessage
            title={i18n.messageFormatter('homeyStatus.nohomey.title')}
            subtitle={i18n.messageFormatter('homeyStatus.nohomey.subtitle')}
          />
        }
      />
    );
  }

  return (
    <Layout
      content={
        <CenteredMessage
          title={i18n.messageFormatter('homeyStatus.notfound.title')}
          subtitle={i18n.messageFormatter('homeyStatus.notfound.subtitle')}
        />
      }
    />
  );
}

function renderImplementError({ auth, selectedHomeyState }) {
  let message = ' ';

  if (auth.error) {
    console.error(auth.error);
    if (auth.error.message) {
      message = ` : ${auth.error.message}`;
    }
  }

  if (selectedHomeyState.error) {
    console.error(selectedHomeyState.error);
    if (selectedHomeyState.error.message) {
      message = ` : ${selectedHomeyState.error.message}`;
    }
  }

  return (
    <Layout
      content={<CenteredMessage title="Error" subtitle={`Unknown error occurred${message}`} />}
    />
  );
}

function LoadingPage(props) {
  return (
    <Layout
      content={
        <CenteredMessage
          icon={<SpinnerLoading color={theme.color.text_light} size={40} />}
          subtitleColor={theme.color.text_light}
          subtitle={props.message}
        />
      }
    />
  );
}

function NoMatch() {
  const location = useLocation();

  return (
    <Layout
      content={
        <CenteredMessage title="Unknown route" subtitle={`No match for ${location.pathname}`} />
      }
    />
  );
}
